/* 
  Table component elements built in accordance with our new Table design specification:

  https://www.figma.com/design/vSpyLjZQbEn9KCClyX5ytF/Information-Architecture?node-id=3762-10650&t=p81yOYj25fQIDqSF-4
*/

import { AllHTMLAttributes, ForwardedRef, forwardRef, useMemo } from 'react';
import classNames from 'classnames';

import { Box } from '../Box/Box';
import { Skeleton } from '../Skeleton/Skeleton';

import * as styles from './styles.css';

const Row = () => (
  <Box
    className={styles.skeletonRowStyle}
    width="full"
    display="flex"
    justifyContent="center"
    alignItems="center"
    padding="medium"
  >
    <Skeleton height="24px" width="100%" />
  </Box>
);

export const TableSkeleton = ({ rowCount = 4 }: { rowCount?: number }) => {
  const rows = useMemo(() => {
    const _rows = [];
    let i = 0;
    while (i < rowCount) {
      _rows.push(<Row key={`tsr-${i}`} />);
      i++;
    }
    return _rows;
  }, [rowCount]);

  return (
    <Box width="full">
      <Box
        width="full"
        display="flex"
        justifyContent="center"
        alignItems="center"
        padding="medium"
        className={styles.skeletonHeaderRowStyle}
      />
      {rows}
    </Box>
  );
};

export const Table = ({ children, className, ...restProps }: AllHTMLAttributes<HTMLTableElement>) => (
  <table {...restProps} className={classNames(styles.tableStyle, className)}>
    {children}
  </table>
);

export const TableHead = ({
  children,
  className,
  sticky = true,
  ...restProps
}: AllHTMLAttributes<HTMLTableSectionElement> & { sticky?: boolean }) => (
  <thead {...restProps} className={classNames(styles.tableHeadStyle({ sticky }), className)}>
    {children}
  </thead>
);

export const TableHeadRow = ({ children, ...restProps }: AllHTMLAttributes<HTMLTableRowElement>) => (
  <tr className={styles.tableHeadRowStyle} {...restProps}>
    {children}
  </tr>
);

export const TableHeadRowCell = ({
  children,
  className,
  ...restProps
}: AllHTMLAttributes<HTMLTableCellElement>) => (
  <th {...restProps} className={classNames(styles.tableHeadCellStyle, className)}>
    {children}
  </th>
);

export const TableBody = forwardRef<HTMLTableSectionElement, AllHTMLAttributes<HTMLTableSectionElement>>(
  ({ children, ...restProps }, ref) => {
    return (
      <tbody ref={ref} {...restProps}>
        {children}
      </tbody>
    );
  }
);
TableBody.displayName = 'TableBody';

interface TableBodyRowProps extends AllHTMLAttributes<HTMLTableRowElement> {
  selected?: boolean;
}

export const TableBodyRow = ({ children, className, selected, ...restProps }: TableBodyRowProps) => (
  <tr {...restProps} className={classNames(styles.tableBodyRowStyle({ selected }), className)}>
    {children}
  </tr>
);

interface TableBodyRowCell extends AllHTMLAttributes<HTMLTableCellElement> {
  selected?: boolean;
}

export const TableBodyRowCell = ({ children, className, selected, ...restProps }: TableBodyRowCell) => (
  <td {...restProps} className={classNames(styles.tableBodyRowCellStyle({ selected }), className)}>
    {children}
  </td>
);
